import { Link } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Flex, Table, Tag } from "antd"
import { ColumnsType } from "antd/es/table"
import jsonToCsvExport from "json-to-csv-export"

import linkIcon from "@src/app/assets/icons/link.svg"
import { IdItem } from "@src/app/components/tableRowItems/idItem"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import {
  MassPayoutPayoutResponseWithExplorerUrl,
  PayoutsCryptoCollection,
} from "@src/pages/payouts/classes/PayoutsCryptoCollection"
import { MassPayoutPayoutResponseStatusEnum } from "@src/shared/api/generated"
import { WithCollection } from "@src/shared/classes/Collection/types"
import { TableFilters } from "@src/shared/componemts/TableFilter"
import { dateTimeFormat } from "@src/shared/date"

type Props = WithCollection<PayoutsCryptoCollection>

export const PayoutsCryptoTable = observer(({ collection }: Props) => {
  const columns: ColumnsType<MassPayoutPayoutResponseWithExplorerUrl> = [
    {
      dataIndex: "id",
      render: (_, { id }) => <IdItem id={id} />,
      title: "ID",
    },
    {
      dataIndex: "external_id",
      title: "External ID",
    },
    {
      dataIndex: "created_at",
      render: (_, { created_at }) => dateTimeFormat(created_at!),
      title: "Date/Time (UTC)",
    },
    {
      dataIndex: "data",
      render: (_, { data }) => {
        const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
          network: data.network as string,
          symbol: data.symbol as string,
        })

        return (
          <span>{`${currency?.symbol_alias ?? data.symbol} (${data.network})`}</span>
        )
      },
      title: "Currency",
    },
    {
      dataIndex: "status",
      render: (_, { status }) => {
        const cMap = {
          [MassPayoutPayoutResponseStatusEnum.Cancelled]: "geekblue",
          [MassPayoutPayoutResponseStatusEnum.Completed]: "green",
          [MassPayoutPayoutResponseStatusEnum.Failed]: "error",
          [MassPayoutPayoutResponseStatusEnum.New]: "geekblue",
          [MassPayoutPayoutResponseStatusEnum.Pending]: "geekblue",
        }

        return <Tag color={cMap[status] ?? "geekblue"}>{status}</Tag>
      },
      title: "Status",
    },
    {
      dataIndex: "data",
      render: (_, { data }) => {
        return <span>{data.amount}</span>
      },
      title: "Amount",
    },
    {
      dataIndex: "data",
      render: (_, { data }) => {
        // @ts-ignore
        return <span>{data.fees?.total_fee}</span>
      },
      title: "Fee",
    },
    {
      dataIndex: "data",
      // @ts-ignore
      render: (_, { data: { amount, fee_payer, fees = { total_fee: 0 } } }) => {
        if (fee_payer === "customer" && fees) {
          return (
            <span>
              {Number.parseFloat(amount) -
                Number.parseFloat(fees.total_fee as string)}
            </span>
          )
        }

        return <span>{amount}</span>
      },
      title: "Amount Credited",
    },
    {
      dataIndex: "data",
      render: (_, { data }) => {
        return <span>{data.fee_payer}</span>
      },
      title: "Fee Payer",
    },
    {
      dataIndex: "data",
      render: (_, { data }) => {
        // @ts-ignore
        return data.ext_tx_id && data.tx_explorer_url ? (
          <Link
            id="GENERATED_ID_PAYOUTSTABLE_LINK_VOID_1"
            rel="noopener noreferrer"
            target="_blank"
            to={`${data.tx_explorer_url}${data.ext_tx_id!}`}
          >
            <img alt="icon" src={linkIcon} />
          </Link>
        ) : (
          "N/A"
        )
      },
      title: "Tx ID",
      width: "5%",
    },
    {
      dataIndex: "error",
      render: (_, { error }) => {
        return error?.code ? (
          <span>{`(${error?.code}) ${error?.message}`}</span>
        ) : null
      },
      title: "Errors",
    },
  ]

  return (
    <Flex gap={32} vertical>
      <TableFilters {...collection.filter.props}></TableFilters>
      <Table
        columns={columns}
        dataSource={collection.withExplorerUrl}
        loading={collection.isLoading}
        pagination={collection.pagination}
        scroll={{ x: 1500 }}
      />

      {Boolean(collection.failed.length) && (
        <Button
          id="GENERATED_ID_PAYOUTSTABLE_BUTTON_DOWNLOAD_FAILED_PAYOUTS_1"
          onClick={() =>
            jsonToCsvExport({
              data: collection.forDownload(collection.failed),
              filename: "failed-payouts.csv",
            })
          }
          style={{ margin: "0 auto", width: "fit-content" }}
        >
          Download Failed Payouts
        </Button>
      )}
    </Flex>
  )
})
